import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faFileContract,
  faPlus,
  faPeopleGroup,
  faSave
} from '@fortawesome/free-solid-svg-icons';
import { ButtonSuccess, Table, TableCell, TableHeader, TableRow } from '../styles/PageStyles';
import { useToast } from '../components/common/ToastProvider';
import { MultiSelectDropdown } from '../components/common/MultiSelect';
import { createCompany, updateCompanyData } from '../services/authService';
import { getCompaniesByFilter } from '../services/companyService';

const Container = styled.div`
  padding: 1rem;
  max-width: 1600px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: start;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 1.9rem;
  font-weight: 700;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-family: 'Orbitron', sans-serif;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin: 0;
`;

const FormLayout = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 3rem;
`;

const StepsMenu = styled.div`
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  height: fit-content;
  box-shadow: 0 5px 15px rgba(0, 102, 255, 0.05);
`;

const StepItem = styled.div`
  padding: 1.2rem;
  border-radius: 12px;
  margin-bottom: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 1rem;
  color: ${props => {
    if (props.active) return 'var(--primary)';
    if (props.completed) return 'var(--success)';
    return 'var(--text-secondary)';
  }};
  background: ${props => {
    if (props.active) return 'rgba(0, 102, 255, 0.08)';
    if (props.completed) return 'rgba(16, 185, 129, 0.05)';
    return 'transparent';
  }};
  border: 1px solid ${props => {
    if (props.active) return 'rgba(0, 102, 255, 0.2)';
    if (props.completed) return 'rgba(16, 185, 129, 0.2)';
    return 'transparent';
  }};
  
  &:hover {
    background: ${props => props.completed || props.active ? '' : 'rgba(0, 102, 255, 0.03)'};
  }
`;

const StepIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  background: ${props => {
    if (props.active) return 'var(--primary)';
    if (props.completed) return 'var(--success)';
    return 'rgba(0, 102, 255, 0.1)';
  }};
  color: ${props => (props.active || props.completed) ? 'white' : 'var(--primary)'};
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 0.3rem;
`;

const StepDescription = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

const FormContent = styled.div`
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 5px 15px rgba(0, 102, 255, 0.05);
`;

const SectionTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const SectionDescription = styled.p`
  color: var(--text-secondary);
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || '1fr 1fr'};
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.6rem;
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.9rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.15);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
  }
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.9rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.15);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
  }
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.9rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.15);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
  }
  
  option {
    background: #ffffff;
    color: var(--text-primary);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2.5rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(0, 102, 255, 0.1);
`;

const Button = styled(motion.button)`
  padding: 0.9rem 2rem;
  background: ${props => {
    if (props.primary) return 'var(--gradient-primary)';
    if (props.secondary) return 'transparent';
    return '#ffffff';
  }};
  border: 1px solid ${props => {
    if (props.primary) return 'transparent';
    if (props.secondary) return 'var(--primary)';
    return 'rgba(0, 102, 255, 0.2)';
  }};
  border-radius: 8px;
  color: ${props => {
    if (props.primary) return 'white';
    if (props.secondary) return 'var(--primary)';
    return 'var(--text-primary)';
  }};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => {
    if (props.primary) return 'var(--gradient-primary)';
    if (props.secondary) return 'rgba(0, 102, 255, 0.05)';
    return 'rgba(0, 102, 255, 0.03)';
  }};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(0, 102, 255, 0.1);
  border-radius: 2px;
  margin-bottom: 2rem;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: var(--gradient-primary);
  border-radius: 2px;
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;


const Spinner = styled.div`
  border: 2px solid #0066ff;   
  border-top: 4px solid #f3f3f3; 
  border-radius: 50%;
  width: 22px;
  height: 22px;
  animation: ${spin} 1s linear infinite;
  margin: auto;
`;

const CompanyCreation = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [userList, setUserList] = useState([]);
  const [showUserform, setShowUserForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const { id } = useParams();

  const initialUserData = {
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    gender: '',
    enabled: true,
    profile_url: '',
    phone_number: null,
    address: '',
    date_of_birth: null,
    bio: '',
    groups: [],
    roles: ['ROLE_COMPANY_ADMIN']
  };

  const [companyData, setCompanyData] = useState({
    name: '',
    status: '',
    description: '',
  });

  const [userData, setUserData] = useState(initialUserData);

  useEffect(() => {
    if (id) {
      const fetchCompany = async () => {
        try {
          // const newId = parseInt(id, 10);
          // const filter = { id: newId };
          const filter = { };
          const response = await getCompaniesByFilter(filter);

          if (response.length) {
            setCompanyData(response[0]);
          }
        } catch (err) {
          console.error("Error fetching company:", err);
        }
      };

      fetchCompany();
    }
  }, [id]);

  const renderUserCreationForm = () => (
    <>
      <SectionTitle>
        <FontAwesomeIcon icon={faFileContract} />
        User Details
      </SectionTitle>
      <SectionDescription>
        Add User to the company.
      </SectionDescription>

      <FormGrid>
        <FormGroup>
          <Label>Username</Label>
          <Input
            type="text"
            placeholder="John Doe"
            value={userData.username}
            onChange={(e) => handleUserInputChange('username', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Email</Label>
          <Input
            type="email"
            placeholder="John@gmail.com"
            value={userData.email}
            onChange={(e) => handleUserInputChange('email', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>First Name</Label>
          <Input
            type="text"
            placeholder="John"
            value={userData.first_name}
            onChange={(e) => handleUserInputChange('first_name', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Last Name</Label>
          <Input
            type="text"
            placeholder="Doe"
            value={userData.last_name}
            onChange={(e) => handleUserInputChange('last_name', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Gender</Label>
          <Select
            value={userData.gender}
            onChange={(e) => handleUserInputChange('gender', e.target.value)}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Roles</Label>
          <MultiSelectDropdown
            options={[
              { value: 'ROLE_COMPANY_ADMIN', label: 'Company Admin' },
              { value: 'ROLE_MANAGER', label: 'Manager' },
              { value: 'ROLE_USER', label: 'User' },
              { value: 'ROLE_VIEWER', label: 'Viewer' }
            ]}
            selected={userData.roles}
            onChange={(newRoles) => handleUserInputChange('roles', newRoles)}
          />
        </FormGroup>
      </FormGrid>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          onClick={() => {
            setShowUserForm(false);
            setIsEdit(false);
            setUserData(initialUserData);
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Cancel
        </Button>
        <div>
          {!isEdit ? (
            <ButtonSuccess
              onClick={addUserToUserList}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FontAwesomeIcon icon={faPlus} />
              Add
            </ButtonSuccess>
          ) : (
            <Button
              primary
              onClick={() => handleSaveUser(userData)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FontAwesomeIcon icon={faSave} style={{paddingRight:'8px'}} />
              Save
            </Button>
          )}
        </div>
      </div>
    </>
  );

  const steps = [
    {
      id: 1,
      title: 'Basic Information',
      description: 'Company name and type',
      icon: faBuilding
    },
    {
      id: 2,
      title: 'User List',
      description: 'Company users Detail list',
      icon: faPeopleGroup
    }
  ];

  const handleCompanyInputChange = (field, value) => {
    setCompanyData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUserInputChange = (field, value) => {
    setUserData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = () => {
    if (!companyData.name || companyData.name.trim() === '') {
      showToast({
        message: "Please add the company name.",
        type: "warning"
      });
      return;
    }
    if (currentStep < steps.length) {
      setCompletedSteps(prev => [...prev, currentStep]);
      setCurrentStep(currentStep + 1);
      setUserData(initialUserData);
      setIsEdit(false);
      setShowUserForm(false); 
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setShowUserForm(false);
      setIsEdit(false);
    }
  };

  const handleStepClick = (stepId) => {
    if (stepId <= currentStep || completedSteps.includes(stepId)) {
      setCurrentStep(stepId);
      setShowUserForm(false);
      setIsEdit(false);
    }
  };

  const handleSubmit = async () => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    try {
      setLoading(true);
      if(!id) {
        const companyPayload = {
        ...companyData,
        company_admins: userList
      };
      
      if(!emailRegex.test(companyPayload.company_admins[0].email)) {
          showToast({
        message: "Please Enter a valid email.",
        type: "warning"
      });
      return;
      }
      
      if(!companyPayload.company_admins.length) {
        showToast({
          message: "Please add at least one user as a company admin before creating a company.",
          type: 'warning'
        });
        return;
      }
      companyPayload.company_admins[0].groups = ['Administrators'];
      const response = await createCompany(companyPayload);
      if (response) {
        showToast({
          message: "Company created successfully.",
          type: 'success'
        });
        setLoading(false);
        navigate('/dashboard');
      }
      } else {
        const response = await updateCompanyData(companyData);
        if (response) {
          showToast({
            message: "Company updated successfully.",
            type: 'success'
          });
          setLoading(false);
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.log("Error while creating the company", error);
      showToast({
        message: "Error creating company.",
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const addUserToUserList = () => {
    if (!userData.username || userData.username.trim() === '' || 
        !userData.email || userData.email.trim() === '') {
      showToast({
        message: 'Please add valid username and email.',
        type: 'warning'
      });
      return;
    }

    const isDuplicate = userList.some(user => 
      user.email === userData.email || user.username === userData.username
    );

    if (isDuplicate) {
      showToast({
        message: 'User with this email or username already exists.',
        type: 'warning'
      });
      return;
    }

    try {
      setUserList(prev => [...prev, { ...userData, id: Date.now() }]);
      setUserData(initialUserData);
      showToast({
        message: "User added successfully",
        type: "success"
      });
      setShowUserForm(false);
    } catch (error) {
      showToast({
        message: "Something went wrong while creating a User",
        type: 'error'
      });
      console.log(error);
    }
  };

  const handleEditUser = (user) => {
    try {
      if (user) {
        setUserData(user);
        setIsEdit(true);
        setShowUserForm(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSaveUser = (userData) => {
    try {
      setUserList(prev => {
        const userIndex = prev.findIndex(user => 
          user.id === userData.id || 
          (user.first_name === userData.first_name && user.email === userData.email)
        );

        if (userIndex === -1) return prev;

        const updatedList = [...prev];
        updatedList[userIndex] = userData;
        return updatedList;
      });
      
      showToast({
        message: "User updated successfully",
        type: "success"
      });
      
      setUserData(initialUserData);
      setIsEdit(false);
      setShowUserForm(false);
    } catch (error) {
      console.log("Something went wrong.", error);
      showToast({
        message: "Error updating user.",
        type: 'error'
      });
    }
  };

  const handleRemoveUser = (userToRemove) => {
    try {
      const filteredUsers = userList.filter(user => 
        user.username !== userToRemove.username || user.email !== userToRemove.email
      );
      setUserList(filteredUsers);
      showToast({
        message: "User removed successfully.",
        type: 'success'
      });
    } catch (error) {
      console.log(error);
      showToast({
        message: "Error removing user.",
        type: 'error'
      });
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <SectionTitle>
              <FontAwesomeIcon icon={faBuilding} />
              Basic Information
            </SectionTitle>
            <SectionDescription>
              Add the basic details about your company.
            </SectionDescription>

            <FormGrid>
              <FormGroup>
                <Label>Company Name *</Label>
                <Input
                  type="text"
                  placeholder="Enter company name"
                  value={companyData.name}
                  onChange={(e) => handleCompanyInputChange('name', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <Label>Status *</Label>
                <Select
                  value={companyData.status}
                  onChange={(e) => handleCompanyInputChange('status', e.target.value)}
                >
                  <option value="">Select Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </Select>
              </FormGroup>
            </FormGrid>

            <FormGroup>
              <Label>Company Description</Label>
              <Textarea
                placeholder="Brief description of your company and services..."
                value={companyData.description}
                onChange={(e) => handleCompanyInputChange('description', e.target.value)}
              />
            </FormGroup>
          </>
        );
      case 2:
        return (
          <>
            {!showUserform ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <SectionTitle>
                    <FontAwesomeIcon icon={faPeopleGroup} />
                    User Details
                  </SectionTitle>
                  {!userList.length && <ButtonSuccess onClick={() => setShowUserForm(true)}>
                    <FontAwesomeIcon icon={faPlus} />
                    Add
                  </ButtonSuccess>}
                </div>
                <SectionDescription>
                  User list of the company.
                </SectionDescription>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>Username</TableHeader>
                      <TableHeader>Email</TableHeader>
                      <TableHeader>First Name</TableHeader>
                      <TableHeader>Last Name</TableHeader>
                      <TableHeader>Actions</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {userList && userList.length > 0 ? (
                      userList.map((user, i) => (
                        <TableRow key={user.id || i}>
                          <TableCell>{user.username}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.first_name}</TableCell>
                          <TableCell>{user.last_name}</TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <Button 
                                primary 
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }} 
                                onClick={() => handleEditUser(user)}
                              >
                                Edit
                              </Button>
                              <Button 
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }} 
                                onClick={() => handleRemoveUser(user)}
                              >
                                Remove
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>
                          No users added yet
                        </TableCell>
                      </TableRow>
                    )}
                  </tbody>
                </Table>
              </div>
            ) : (
              renderUserCreationForm()
            )}
          </>
        );
      default:
        return null;
    }
  };

  const progress = (currentStep / steps.length) * 100;

  return (
    <Container>
      <Header>
        <Title>{id ? 'Edit Company' : 'Create New Company'}</Title>
      </Header>

      <ProgressBar>
        <ProgressFill progress={progress} />
      </ProgressBar>

      <FormLayout>
        <StepsMenu>
          {steps.map((step) => (
            <StepItem
              key={step.id}
              active={currentStep === step.id}
              completed={completedSteps.includes(step.id)}
              onClick={() => handleStepClick(step.id)}
            >
              <StepIcon
                active={currentStep === step.id}
                completed={completedSteps.includes(step.id)}
              >
                <FontAwesomeIcon icon={step.icon} />
              </StepIcon>
              <StepContent>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepContent>
            </StepItem>
          ))}
        </StepsMenu>

        <FormContent>
          {renderStepContent()}

          {!showUserform && (
            <ButtonGroup>
              <div>
                {currentStep > 1 && (
                  <Button
                    onClick={handlePrevious}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Previous
                  </Button>
                )}
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button
                  secondary
                  onClick={() => navigate('/dashboard')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </Button>

                {currentStep < steps.length ? (
                  <Button
                    primary
                    onClick={handleNext}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Next Step
                  </Button>
                ) : (
                    <Button
                      primary
                      onClick={handleSubmit}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div style={{ display: 'flex', gap: '10px' }}>
                        {loading && <Spinner />}
                        {id ? 'Update Company' : 'Create Company'}
                      </div>
                    </Button>
                )}
              </div>
            </ButtonGroup>
          )}
        </FormContent>
      </FormLayout>
    </Container>
  );
};

export default CompanyCreation;